import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock, ExternalLink, Copy, Check, Undo2, Loader2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS = { paid: "bg-[#DCFCE7] text-[#166534]", overdue: "bg-[#FEE2E2] text-[#991B1B]", next: "bg-[#FEF3C7] text-[#92600A]", upcoming: "bg-[#F4F5F3] text-[#595D5A]" };

export const QuarterlyPayments = ({ year, refreshKey, onChanged }) => {
  const [data, setData] = useState(null);
  const [openQ, setOpenQ] = useState(null);
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/vault/taxpal/payments", { params: { year } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [year, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data || !data.has_profile) return null;

  const copyAmt = (v) => { navigator.clipboard?.writeText(Number(v).toFixed(2)); toast.success(`${money(v)} copied — paste it into IRS Direct Pay`); };
  const payNow = (q) => { copyAmt(q.amount); window.open(data.links.irs_direct_pay, "_blank", "noopener"); };
  const markPaid = async (q) => {
    const a = parseFloat(amt || q.amount);
    if (!a || a <= 0) { toast.error("Enter the amount you paid"); return; }
    setBusy(q.label);
    try { await api.post(`/vault/taxpal/payments/${year}/${q.label}/mark-paid`, { amount: a }); toast.success(`${q.label} marked paid · your estimate now counts ${money(a)}`); setOpenQ(null); setAmt(""); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(null); }
  };
  const unmark = async (q) => {
    setBusy(q.label);
    try { await api.delete(`/vault/taxpal/payments/${year}/${q.label}`); load(); onChanged?.(); } catch { toast.error("Could not undo"); } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="quarterly-payments">
      <div className="flex items-center gap-2 text-[#114A31]"><CalendarClock className="h-5 w-5" /><span className="font-heading font-semibold">Quarterly estimated taxes · {year}</span></div>
      <p className="mt-1 text-sm text-[#595D5A]">Tax Pal's set-aside per quarter. "Pay IRS now" copies the exact amount and opens IRS Direct Pay — the IRS doesn't allow pre-filled amounts, so paste it in.</p>
      <ul className="mt-4 space-y-2" data-testid="quarters-list">
        {data.quarters.map((q) => (
          <li key={q.label} className="bg-[#F9F9F8] rounded-xl px-3 py-2.5" data-testid={`quarter-${q.label}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS[q.status]}`} data-testid={`quarter-status-${q.label}`}>{q.status}</span>
              <span className="text-sm font-semibold text-[#1C1C1C]">{q.label}</span>
              <span className="text-xs text-[#595D5A]">due {new Date(q.due + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
              <span className="ml-auto font-heading font-semibold text-[#1C1C1C]" data-testid={`quarter-amount-${q.label}`}>{q.paid ? money(q.paid_amount) : money(q.amount)}</span>
            </div>
            {q.state_amount > 0 && !q.paid && <p className="text-[11px] text-[#595D5A] mt-1">+ about {money(q.state_amount)} to {data.state_name} (pay on your state's site)</p>}
            <div className="mt-2 flex gap-2 flex-wrap">
              {q.paid ? (
                <Button onClick={() => unmark(q)} disabled={busy === q.label} size="sm" variant="ghost" data-testid={`quarter-unmark-${q.label}`} className="rounded-full h-8 text-xs text-[#595D5A]"><Undo2 className="h-3 w-3 mr-1" /> Undo</Button>
              ) : (
                <>
                  <Button onClick={() => payNow(q)} size="sm" data-testid={`quarter-pay-${q.label}`} className="rounded-full h-8 text-xs bg-[#114A31] hover:bg-[#1A6B46] text-white"><ExternalLink className="h-3 w-3 mr-1" /> Pay IRS now</Button>
                  <Button onClick={() => copyAmt(q.amount)} size="sm" variant="outline" data-testid={`quarter-copy-${q.label}`} className="rounded-full h-8 text-xs border-[#E5E5E5]"><Copy className="h-3 w-3 mr-1" /> Copy amount</Button>
                  <Button onClick={() => { setOpenQ(openQ === q.label ? null : q.label); setAmt(String(q.amount)); }} size="sm" variant="ghost" data-testid={`quarter-markpaid-${q.label}`} className="rounded-full h-8 text-xs text-[#114A31]"><Check className="h-3 w-3 mr-1" /> I paid this</Button>
                </>
              )}
            </div>
            {openQ === q.label && (
              <div className="mt-2 flex gap-2" data-testid={`quarter-markpaid-form-${q.label}`}>
                <Input type="number" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} data-testid={`quarter-paid-amount-${q.label}`} className="h-9 rounded-xl" />
                <Button onClick={() => markPaid(q)} disabled={busy === q.label} size="sm" data-testid={`quarter-markpaid-save-${q.label}`} className="rounded-full h-9 bg-[#114A31] text-white">{busy === q.label ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-[#595D5A]">On Direct Pay: {data.steps.join(" → ")}. Prefer EFTPS? <a href={data.links.eftps} target="_blank" rel="noreferrer" className="text-[#114A31] underline">eftps.gov</a></p>
    </div>
  );
};

export default QuarterlyPayments;
